/**
 * Seeds a stamp's content with the appointment's reason for visit.
 *
 * Stamps are free text written by each clinic, so the chief-complaint anchor we
 * look for ("cc:", failing that the "S/" subjective header) is not guaranteed to
 * be there. When neither is present the reason is prepended rather than dropped:
 * silently losing the patient's reason for visit is worse than a stamp that
 * starts with an extra line.
 */
export function applyReasonToStamp(stampContent: string | undefined, reason?: string): string {
  const content = stampContent ?? '';

  if (!reason) return content;

  if (!content) return `S/\ncc: ${reason}\n`;

  if (content.includes('cc:')) return content.replace('cc:', `cc: "${reason}"`);

  if (content.includes('S/')) return content.replace('S/', `S/\ncc: ${reason}\n`);

  return `cc: ${reason}\n${content}`;
}

/** Matches a chief-complaint line and captures whatever the clinician wrote after it. */
const CHIEF_COMPLAINT_LINE = /^[ \t]*c\/?c[ \t]*:(.*)$/im;

/**
 * True when the note already states the reason for visit — either verbatim, or as
 * a chief-complaint line the clinician has already filled in with something of
 * their own. Quotes and case are ignored, since `applyReasonToStamp` writes the
 * reason quoted and the clinician may have retyped it.
 */
function noteAlreadyStatesReason(note: string, reason: string): boolean {
  const normalize = (text: string) => text.replace(/["']/g, '').trim().toLowerCase();

  if (normalize(note).includes(normalize(reason))) return true;

  const [, existingComplaint] = note.match(CHIEF_COMPLAINT_LINE) ?? [];

  return !!existingComplaint && !!normalize(existingComplaint);
}

/**
 * Seeds a stamp being pasted into a note that is already in progress. Same rules
 * as `applyReasonToStamp`, except the reason is left out when the note already
 * carries it — pasting a stamp should not duplicate a chief complaint, nor
 * suggest overwriting one the clinician wrote themselves.
 */
export function applyReasonToInsertedStamp(
  stampContent: string | undefined,
  reason: string | undefined,
  note: string | undefined,
): string {
  const content = stampContent ?? '';

  if (!reason) return content;
  if (noteAlreadyStatesReason(note ?? '', reason)) return content;

  return applyReasonToStamp(content, reason);
}
